import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Alert } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { API_DOMAIN } from "../components/constants";
import { useAuth } from "../components/contexts/AuthContext";
import uzdLogo from "../assets/images/uzd_logo_small.jpeg";

const styleTag = `
  @keyframes login-fade-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes login-glow-red {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%      { transform: translate(40px, 30px) scale(1.15); }
  }
  @keyframes login-glow-green {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%      { transform: translate(-40px, -30px) scale(1.12); }
  }
  .login-card-input .ant-input-affix-wrapper {
    background: rgba(255,255,255,0.06) !important;
    border: 1px solid rgba(255,255,255,0.18) !important;
    border-radius: 10px !important;
  }
  .login-card-input .ant-input-affix-wrapper:hover {
    border-color: rgba(225,29,46,0.7) !important;
  }
  .login-card-input .ant-input-affix-wrapper-focused {
    border-color: #22c55e !important;
    box-shadow: 0 0 0 3px rgba(34,197,94,0.18) !important;
  }
  .login-card-input input {
    background: transparent !important;
    color: #f1f5f9 !important;
  }
  .login-card-input input::placeholder { color: rgba(241,245,249,0.4); }
  .login-card-input .anticon { color: rgba(241,245,249,0.5); }
  .login-card-input .ant-input-password-icon { color: rgba(241,245,249,0.5) !important; }
  .login-signin-btn:hover {
    filter: brightness(1.1);
  }
`;

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);       // { type: 'error' | 'warning', text }

  const onFinish = async ({ username, password }) => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API_DOMAIN}/auth/login`, { username: username.trim(), password });
      if (res.data.error) {
        if (res.data.error === "approval") {
          setError({ type: "warning", text: "Your account is awaiting admin approval. You'll be able to sign in once it's activated." });
        } else {
          setError({ type: "error", text: typeof res.data.error === "string" ? res.data.error : "Invalid credentials." });
        }
        return;
      }
      login({
        id: res.data.id,
        token: res.data.token,
        user: {
          username: res.data.username,
          name: res.data.name,
          email: res.data.email,
          admin: res.data.admin,
          wati_msg_approval: res.data.wati_msg_approval,
          wati_id: res.data.wati_id,
        },
      });
      navigate("/", { replace: true });
    } catch {
      setError({ type: "error", text: "Could not reach the server. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(160deg, #0a0a0a 0%, #141414 55%, #0f1a0f 100%)",
      }}
    >
      <style>{styleTag}</style>

      {/* Ambient brand glows — red top-left, green bottom-right */}
      <div style={{
        position: "absolute", top: "-15%", left: "-10%",
        width: 460, height: 460, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(225,29,46,0.28) 0%, transparent 65%)",
        animation: "login-glow-red 14s ease-in-out infinite",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-18%", right: "-10%",
        width: 520, height: 520, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(34,197,94,0.22) 0%, transparent 65%)",
        animation: "login-glow-green 16s ease-in-out infinite",
        pointerEvents: "none",
      }} />

      <div
        style={{
          width: "100%",
          maxWidth: 400,
          position: "relative",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderTop: "3px solid #e11d2e",
          borderRadius: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
          padding: "40px 32px 32px",
          animation: "login-fade-up 0.5s cubic-bezier(0.22,1,0.36,1) both",
        }}
      >
        {/* Brand — logo on a white plate (logo artwork has a white background) */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              display: "inline-block",
              background: "#fff",
              borderRadius: 14,
              padding: "12px 22px",
              marginBottom: 16,
              boxShadow: "0 10px 30px rgba(225,29,46,0.28), 0 4px 14px rgba(0,0,0,0.4)",
            }}
          >
            <img
              src={uzdLogo}
              alt="UZDPART"
              style={{ height: 54, display: "block", objectFit: "contain" }}
            />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", letterSpacing: 4, textTransform: "uppercase" }}>
            <span style={{ color: "#e11d2e" }}>S</span>upplier <span style={{ color: "#e11d2e" }}>R</span>elationship <span style={{ color: "#e11d2e" }}>M</span>anager
          </div>
          <div style={{ fontSize: 13, color: "rgba(241,245,249,0.55)", marginTop: 6 }}>
            Sign in to your workspace
          </div>
        </div>

        {error && (
          <Alert
            type={error.type}
            message={error.text}
            showIcon
            style={{ marginBottom: 18, borderRadius: 10 }}
          />
        )}

        <Form layout="vertical" onFinish={onFinish} requiredMark={false} className="login-card-input">
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Enter your username" }, { min: 3, message: "At least 3 characters" }]}
            style={{ marginBottom: 16 }}
          >
            <Input
              size="large"
              prefix={<UserOutlined />}
              placeholder="Username"
              autoComplete="username"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Enter your password" }, { min: 4, message: "At least 4 characters" }]}
            style={{ marginBottom: 22 }}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loading}
            className="login-signin-btn"
            style={{
              height: 46, borderRadius: 10, fontWeight: 700, letterSpacing: 0.5,
              background: "linear-gradient(135deg, #22c55e, #15803d)",
              border: "none",
              boxShadow: "0 8px 22px rgba(34,197,94,0.35)",
            }}
          >
            Sign In
          </Button>
        </Form>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: "rgba(241,245,249,0.35)" }}>
          UzdPart Used Spare Parts Trading LLC © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

export default Login;
